import React from "react";
import { Loader2, Mail, Phone, Trash2, Bell, Send, Archive } from "lucide-react";
import api from "../../lib/api";

const STATUS_LABEL = {
  waiting: { label: "En attente", cls: "bg-amber-500/15 text-amber-200 border-amber-400/30" },
  notified: { label: "Notifié", cls: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30" },
  converted: { label: "Converti", cls: "bg-accent-deep/15 text-accent-light border-accent-deep/30" },
  archived: { label: "Archivé", cls: "bg-white/5 text-text-cream/40 border-white/10" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-CH", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function AdminWaitlist() {
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [statusFilter, setStatusFilter] = React.useState("");
  const [busyId, setBusyId] = React.useState("");

  const load = React.useCallback(() => {
    setLoading(true);
    const qs = statusFilter ? `?status=${statusFilter}` : "";
    api.get(`/admin/waitlist${qs}`)
      .then(r => setEntries(r.data))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  React.useEffect(() => { load(); }, [load]);

  const notifyNow = async (e) => {
    if (!e.date) {
      alert("Cette entrée n'a pas de date précise — contacte le client manuellement.");
      return;
    }
    setBusyId(e.id);
    try {
      const r = await api.post("/admin/waitlist/notify", {
        service_slug: e.service_slug,
        date: e.date,
      });
      alert(`Notification envoyée (${r.data.notified} email${r.data.notified > 1 ? "s" : ""} envoyé${r.data.notified > 1 ? "s" : ""})`);
      load();
    } catch (err) {
      alert("Erreur envoi notification");
    } finally {
      setBusyId("");
    }
  };

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/waitlist/${id}`, { status });
      load();
    } catch {
      alert("Erreur mise à jour");
    } finally {
      setBusyId("");
    }
  };

  const deleteEntry = async (id) => {
    if (!confirm("Supprimer cette entrée ?")) return;
    try {
      await api.delete(`/admin/waitlist/${id}`);
      load();
    } catch {
      alert("Erreur suppression");
    }
  };

  const counts = React.useMemo(() => {
    const c = { waiting: 0, notified: 0, converted: 0, archived: 0, total: entries.length };
    for (const e of entries) c[e.status] = (c[e.status] || 0) + 1;
    return c;
  }, [entries]);

  return (
    <div className="text-text-cream" data-testid="admin-waitlist">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Admin</div>
          <h1 className="font-serif text-4xl">Liste d'attente</h1>
          <p className="text-text-cream/50 mt-2 text-sm font-light max-w-2xl">
            Les clients inscrits reçoivent automatiquement un email dès qu'un créneau se libère
            (annulation d'une réservation existante). Tu peux aussi notifier manuellement.
          </p>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {[
          { key: "", label: `Toutes (${counts.total})` },
          { key: "waiting", label: `En attente (${counts.waiting})` },
          { key: "notified", label: `Notifié (${counts.notified})` },
          { key: "converted", label: `Converti (${counts.converted})` },
          { key: "archived", label: `Archivé (${counts.archived})` },
        ].map(f => (
          <button
            key={f.key || "all"}
            onClick={() => setStatusFilter(f.key)}
            className={`text-xs px-3 py-1.5 rounded border transition ${
              statusFilter === f.key
                ? "border-accent-deep bg-accent-deep/15 text-accent-light"
                : "border-white/10 text-text-cream/60 hover:border-accent-deep/40 hover:text-text-cream"
            }`}
            data-testid={`waitlist-filter-${f.key || "all"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="text-text-cream/60 text-sm font-light inline-flex items-center gap-2" data-testid="waitlist-loading">
          <Loader2 size={14} className="animate-spin" /> Chargement…
        </div>
      )}

      {!loading && entries.length === 0 && (
        <div className="text-text-cream/50 text-sm font-light italic" data-testid="waitlist-empty">
          Aucune entrée. Les inscriptions apparaîtront ici.
        </div>
      )}

      {!loading && entries.length > 0 && (
        <div className="space-y-3">
          {entries.map(e => {
            const meta = STATUS_LABEL[e.status] || STATUS_LABEL.waiting;
            const busy = busyId === e.id;
            return (
              <div
                key={e.id}
                className="bg-white/[0.03] border border-white/10 rounded p-5 flex flex-col lg:flex-row lg:items-center gap-4"
                data-testid={`waitlist-entry-${e.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <div className="font-serif text-lg">
                      {e.prenom} {e.nom}
                    </div>
                    <span className={`text-[0.6rem] uppercase tracking-[0.18em] border px-2 py-0.5 rounded ${meta.cls}`}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="text-text-cream/70 text-sm font-light">
                    <strong className="text-accent-light">{e.service_name}</strong>
                    {" · "}
                    {e.date ? formatDate(e.date) : <span className="italic text-text-cream/50">Toute date</span>}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-text-cream/50 font-light flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <Mail size={11} />
                      <a href={`mailto:${e.email}`} className="hover:text-accent-light">{e.email}</a>
                    </span>
                    {e.tel && (
                      <span className="inline-flex items-center gap-1">
                        <Phone size={11} />
                        <a href={`tel:${e.tel}`} className="hover:text-accent-light">{e.tel}</a>
                      </span>
                    )}
                    <span>Inscrit le {formatDateTime(e.created_at)}</span>
                    {e.notified_at && (
                      <span className="text-emerald-300/70">Notifié le {formatDateTime(e.notified_at)}</span>
                    )}
                  </div>
                  {e.notes && (
                    <div className="text-xs text-text-cream/60 italic font-light mt-2 pt-2 border-t border-white/5">
                      {e.notes}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap shrink-0">
                  {e.status === "waiting" && e.date && (
                    <button
                      onClick={() => notifyNow(e)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-accent-deep/40 bg-accent-deep/10 text-accent-light hover:bg-accent-deep/20 transition disabled:opacity-50"
                      data-testid={`waitlist-notify-${e.id}`}
                      title="Envoyer email de notification maintenant"
                    >
                      {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      Notifier
                    </button>
                  )}
                  {e.status !== "converted" && (
                    <button
                      onClick={() => updateStatus(e.id, "converted")}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 transition disabled:opacity-50"
                      data-testid={`waitlist-convert-${e.id}`}
                    >
                      <Bell size={12} /> Converti
                    </button>
                  )}
                  {e.status !== "archived" && (
                    <button
                      onClick={() => updateStatus(e.id, "archived")}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-white/15 text-text-cream/60 hover:text-text-cream hover:border-white/30 transition disabled:opacity-50"
                      data-testid={`waitlist-archive-${e.id}`}
                    >
                      <Archive size={12} /> Archiver
                    </button>
                  )}
                  <button
                    onClick={() => deleteEntry(e.id)}
                    className="inline-flex items-center justify-center p-2 rounded border border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 transition"
                    data-testid={`waitlist-delete-${e.id}`}
                    title="Supprimer"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
